import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase.ts';
import { Payment, PaymentWithDetails } from '../types/payment.ts';

export const usePayments = () => {
  const [payments, setPayments] = useState<PaymentWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isRefetching, setIsRefetching] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .select(`
          *,
          tenant:tenants(full_name),
          property:properties(name),
          bill:bills(month)
        `)
        .order('payment_date', { ascending: false });

      if (error) throw error;
      setPayments(data || []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const refetchPayments = async () => {
    setIsRefetching(true);
    try {
      await fetchPayments();
    } finally {
      setIsRefetching(false);
    }
  };

  const addPayment = async (paymentData: Omit<Payment, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .insert([paymentData])
        .select()
        .single();

      if (error) throw error;

      // Update bill status
      await supabase
        .from('bills')
        .update({ status: 'Completed', payment_date: paymentData.payment_date })
        .eq('id', paymentData.bill_id);

      await fetchPayments(); // Refresh the list
      return data;
    } catch (err) {
      throw err;
    }
  };

  const deletePayment = async (id: string, billId: string) => {
    try {
      const { error } = await supabase
        .from('payments')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Reset bill status
      await supabase
        .from('bills')
        .update({ status: 'Due', payment_date: null })
        .eq('id', billId);

      setPayments(payments.filter(p => p.id !== id));
    } catch (err) {
      throw err;
    }
  };

  return {
    payments,
    loading,
    error,
    addPayment,
    deletePayment,
    refresh: fetchPayments,
    refetchPayments,
    isRefetching,
  };
};
